import type { TooltipProps } from '@mui/material'
import { useCallback, useState } from 'react'
import { MolsonButton } from '../molson-button'
import { MolsonDivider } from '../molson-divider'
import { type IconColor, type IconVariant, MolsonIcon } from '../molson-icons'
import { MolsonMenu } from '../molson-menu'
import { MolsonMenuItem } from '../molson-menu-item'
import { MolsonTooltip } from '../molson-tooltip'
import { MolsonTypography } from '../molson-typography'
import { molsonMenuItemStyles, molsonMenuStyles } from './styles'

type TooltipPlacement = TooltipProps['placement']

interface Action {
  disabled?: boolean
  iconVariant?: IconVariant
  iconColor?: IconColor
  label: string
  onAction: () => void | Promise<void>
  testId?: string
  tooltipMessage?: React.ReactNode | string
  tooltipPosition?: TooltipPlacement
  visible?: boolean
}

export interface MoreActionsProps {
  actions: Action[]
  isMoreIconDisabled?: boolean
  style?: React.CSSProperties
  dataTestId?: string
  color?: 'secondary' | 'primary'
  variant?: 'contained' | 'outlined' | 'text'
  label?: string
  displayTooltip?: boolean
  iconType?: IconVariant
}

export const MolsonMoreActions = ({
  actions,
  dataTestId,
  isMoreIconDisabled = false,
  style,
  color = 'secondary',
  variant = 'text',
  label = '',
  displayTooltip = false,
  iconType = 'MORE_VERT'
}: MoreActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleAction = async (onAction: () => void | Promise<void>) => {
    await onAction()
    setAnchorEl(null)
  }

  const handleIconType = useCallback(() => {
    if (iconType === 'ARROW_DROP_DOWN') {
      if (anchorEl) {
        return 'ARROW_DROP_UP'
      }
    }
    return iconType
  }, [anchorEl, iconType])

  return (
    <>
      <MolsonButton
        color={color}
        disabled={isMoreIconDisabled}
        hasIconOnly={!label}
        onClick={handleOpenMenu}
        style={style}
        variant={variant}
        dataTestId={dataTestId}
      >
        {label}
        <MolsonIcon variant={handleIconType()} />
      </MolsonButton>
      <MolsonMenu
        anchorEl={anchorEl}
        disableAutoFocusItem={true}
        keepMounted={false}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        sx={molsonMenuStyles}
      >
        {actions.map(
          (
            {
              disabled,
              iconVariant,
              iconColor,
              label,
              onAction,
              testId,
              tooltipMessage,
              tooltipPosition,
              visible = true
            },
            index
          ) => (
            <div key={`${testId}-${index}`} hidden={!visible}>
              {displayTooltip ? (
                <MolsonTooltip
                  title={tooltipMessage}
                  placement={tooltipPosition}
                >
                  <MolsonMenuItem
                    disabled={disabled}
                    onClick={() => handleAction(onAction)}
                    sx={molsonMenuItemStyles}
                    data-testid={testId}
                  >
                    <MolsonTypography variant="h4">{label}</MolsonTypography>
                    {iconVariant && (
                      <MolsonIcon variant={iconVariant} iconColor={iconColor} />
                    )}
                  </MolsonMenuItem>
                </MolsonTooltip>
              ) : (
                <MolsonMenuItem
                  disabled={disabled}
                  onClick={() => handleAction(onAction)}
                  sx={molsonMenuItemStyles}
                  data-testid={testId}
                >
                  <MolsonTypography variant="h4">{label}</MolsonTypography>
                  {iconVariant && (
                    <MolsonIcon variant={iconVariant} iconColor={iconColor} />
                  )}
                </MolsonMenuItem>
              )}
              {index < actions.length - 1 && (
                <MolsonDivider sx={{ m: '0 !important' }} />
              )}
            </div>
          )
        )}
      </MolsonMenu>
    </>
  )
}
